import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { PaddingProps } from 'styled-system';

import { StickyHeaderLinkData } from 'api/cms';
import Box from 'components/Box';
import { Button } from 'components/Button';
import Flex from 'components/Flex';
import Hide from 'components/Hide';
import Icon from 'components/Icon/Icon';
import { IdealistLogo } from 'components/Logo';
import { BodySmall, H3, H4, H5 } from 'components/Text';
import ScrollWatcherContainer from 'containers/ScrollWatcherContainer';
import { bp, breakpoints, colors, gutterWidth, transition } from 'theme';
import scrollIntoView from 'utils/scrollIntoView';

const StickyHeaderLinksContainer = styled(
  ({
    isSticky,
    ...props
  }: { isSticky: boolean; children: ReactNode } & PaddingProps) => (
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Box {...props} />
  )
).attrs(({ isSticky }) => ({
  style: {
    transform: isSticky
      ? 'translateY(0)'
      : 'translateY(-100%) translateY(-8px)',
  },
}))`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background-color: ${colors.white};
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.075);
  transition: transform 0.45s ease;
`;
const IconContainer = styled(Box)`
  transition: all ${transition};
  * {
    fill: currentColor;
  }
`;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const HeaderLinkTitle = ({ isSticky, ...props }: { isSticky: boolean }) =>
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/jsx-props-no-spreading
  isSticky ? <H4 {...props} /> : <H3 {...props} />;

const HeaderLink = styled(({ className, link, isSticky }) => {
  const onClick = () => {
    const element = document.getElementById(link.url);

    if (element) {
      scrollIntoView(element);
    } else {
      // @ts-expect-error ts-migrate(2322) FIXME: Type 'string' is not assignable to type 'Location'... Remove this comment to see the full error message
      window.location = `#${link.url}`;
    }
  };

  return (
    <li className={className} key={`${isSticky ? 'sticky-' : ''}${link.url}`}>
      <Button onClick={onClick} color={colors.waterBlue} py={20} height="100%">
        {/* @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: any; tag: string; isSticky: any;... Remove this comment to see the full error message */}
        <HeaderLinkTitle tag="div" isSticky={isSticky} lineHeight={1.25}>
          {link.heading}
        </HeaderLinkTitle>
        <Hide xs sm md>
          {!isSticky && link.subHeading && link.subHeading.length > 0 && (
            <BodySmall>{link.subHeading}</BodySmall>
          )}
          <IconContainer mt={isSticky ? undefined : '16px'}>
            <Icon name="arrow" width={10} direction="left" />
          </IconContainer>
        </Hide>
      </Button>
    </li>
  );
})`
  position: relative;
  align-items: center;
  display: flex;
  height: 100%;
  flex: 1 1 auto;
  justify-content: center;
  text-align: center;

  button {
    transition: all ${transition};

    &:hover,
    &:focus {
      color: ${colors.waterBlue};

      ${IconContainer} {
        transform: translateY(6px);
      }
    }
  }

  ${({ isSticky }) =>
    isSticky
      ? css`
          margin: 0 24px;
          height: 104px;
          border: none;

          ${IconContainer} {
            position: absolute;
            bottom: 6px;
            left: 0;
            right: 0;
            margin: 0 auto;
          }
        `
      : css`
          @media (max-width: ${bp[2] - 1}px) {
            margin-top: 12px;

            &:last-child {
              margin-bottom: 12px;
            }
          }

          @media (min-width: ${breakpoints[2]}) {
            width: calc(
              100% /
                ${(props) =>
                  // @ts-expect-error Fix type
                  props.total}
            );
            height: 175px;

            button {
              display: flex;
              flex-direction: column;
              justify-content: flex-end;
              align-items: center;
              width: 100%;
              height: 100%;
              padding: 20px 20px 32px;
            }

            &:not(:first-child) {
              &::before {
                content: '';
                position: absolute;
                left: 0;
                top: 50%;
                transform: translateY(-50%);
                width: 1px;
                height: calc(100% - 48px);
                background: ${colors.aluminum};
              }
            }

            h3 {
              font-size: 22px;
            }
          }
        `}
`;
const HeaderLinks = styled(
  ({
    className,
    links,
    isSticky,
  }: {
    className?: string;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    links: Array<Record<string, any>>;
    isSticky?: boolean;
  }) => (
    <nav className={className}>
      <ul>
        {links.map((link, index) => (
          <HeaderLink
            link={link}
            isSticky={isSticky}
            total={links.length} // eslint-disable-next-line react/no-array-index-key
            key={`${index}-${link.url}`}
          />
        ))}
      </ul>
    </nav>
  )
)<{ hasButton?: boolean }>`
  width: 100%;

  ul {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    list-style-type: none;
    margin: 0;
    padding: 0;
  }

  @media (min-width: ${breakpoints[2]}) {
    ul {
      flex-direction: row;
    }
  }

  ${(props) =>
    props.isSticky &&
    css`
      display: none;

      @media (min-width: ${breakpoints[2]}) {
        display: flex;
        justify-content: center;
        padding-right: ${props.hasButton ? 0 : 160}px;
      }
    `};
`;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const StickyHeaderLinks = ({
  links,
  button,
}: {
  links: Array<StickyHeaderLinkData>;
  button?: ReactNode;
}) => {
  const hasButton = typeof button !== 'undefined';
  return (
    <>
      <Box boxShadow="0 2px 6px 0 rgba(0, 0, 0, 0.075)">
        <Hide lg xl>
          <Box mb="12px" pt="12px">
            <H5 color={colors.battleshipGrey} textAlign="center">
              On this page
            </H5>
          </Box>
        </Hide>
        <HeaderLinks links={links} />
      </Box>
      <ScrollWatcherContainer>
        {({ isFullyAboveViewport: isSticky }) => (
          <Hide xs sm md>
            <StickyHeaderLinksContainer
              isSticky={isSticky}
              py={14}
              px={gutterWidth / 2}
            >
              <Box component="a" href="/" flex="0 0 auto">
                <IdealistLogo height={49} />
              </Box>
              <HeaderLinks links={links} isSticky hasButton={hasButton} />
              {hasButton && (
                <Flex minWidth={160} justifyContent="flex-end">
                  {button}
                </Flex>
              )}
            </StickyHeaderLinksContainer>
          </Hide>
        )}
      </ScrollWatcherContainer>
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default StickyHeaderLinks;
